import { createFeature, createReducer, on } from '@ngrx/store';

import { PaymentMethodState } from '../../interfaces/payment-method';
import * as PaymentMethodActions from './payment-method.actions';

export const PaymentMethodFeatureName = 'PaymentMethod';

export const paymentMethodInitialState: PaymentMethodState = {
    backupMethods: {},
    loading: false,
    session: null,
    saving: false,
    saved: false,
    error: null,
};

export const PaymentMethodFeature = createFeature({
    name: PaymentMethodFeatureName,
    reducer: createReducer(
        paymentMethodInitialState,

        on(PaymentMethodActions.loadBackupMethod, (state): PaymentMethodState => ({
            ...state,
            loading: true,
            error: null,
        })),

        // A null method is a loaded state, not a missing one: the ROSE simply has no card yet,
        // and writing the key records that we asked and got an answer.
        on(PaymentMethodActions.loadBackupMethodSuccess, (state, { leaseKey, backupMethod }): PaymentMethodState => ({
            ...state,
            backupMethods: { ...state.backupMethods, [leaseKey]: backupMethod },
            loading: false,
        })),

        /**
         * The lease is left absent from the map rather than written as `null`: a lookup that
         * failed is not a lease without a card, and recording it as one would let a dropped
         * request tell UserPilot she needs a backup she may well already have.
         */
        on(PaymentMethodActions.loadBackupMethodError, (state, { error }): PaymentMethodState => ({
            ...state,
            loading: false,
            error,
        })),

        // Clears saved: opening a fresh capture means the previous success is history. Left
        // set, a remounted AddCardComponent reads it and bounces straight back to the list.
        on(PaymentMethodActions.openTokenizeSession, (state): PaymentMethodState => ({
            ...state,
            loading: true,
            session: null,
            saved: false,
            error: null,
        })),

        on(PaymentMethodActions.openTokenizeSessionSuccess, (state, { session }): PaymentMethodState => ({
            ...state,
            session,
            loading: false,
        })),

        on(PaymentMethodActions.openTokenizeSessionError, (state, { error }): PaymentMethodState => ({
            ...state,
            loading: false,
            error,
        })),

        // Stays true across the round trip so the card shows a spinner until PNM's result lands.
        on(PaymentMethodActions.saveBackupMethod, (state): PaymentMethodState => ({
            ...state,
            saving: true,
            saved: false,
            error: null,
        })),

        /**
         * Writing the saved card straight into the map is what flips her UserPilot property to
         * `active` in the same session (OPS-2288) — the roll-up recomputes off this map, so no
         * re-fetch and no re-login stand between saving a card and the nudge stopping.
         */
        on(PaymentMethodActions.saveBackupMethodSuccess, (state, { leaseKey, backupMethod }): PaymentMethodState => ({
            ...state,
            backupMethods: { ...state.backupMethods, [leaseKey]: backupMethod },
            saving: false,
            saved: true,
            session: null,
        })),

        on(PaymentMethodActions.saveBackupMethodError, (state, { error }): PaymentMethodState => ({
            ...state,
            saving: false,
            saved: false,
            error,
        })),

        // Only the error: the session and the card map belong to the modal, which is still open.
        on(PaymentMethodActions.clearPaymentMethodError, (state): PaymentMethodState => ({
            ...state,
            error: null,
        })),

        /**
         * Closing the modal clears its own working state — the session, the error, the saved
         * flag — but keeps the card map. That map outlives any one modal: it is the fresher
         * half of what UserPilot's property is derived from, and emptying it under her would
         * put the nudge back on a ROSE who just added a card.
         */
        on(PaymentMethodActions.resetPaymentMethodState, (state): PaymentMethodState => ({
            ...paymentMethodInitialState,
            backupMethods: state.backupMethods,
        })),
    ),
});
